<template>
  <loading-indicator
      v-if="loading"
      :size="48"
      class="d-flex flex-center p-6"/>
  <job-request-view v-else :is-zipcode-chosen="isZipcodeChosen" />
</template>

<script>
/* eslint-disable camelcase */

import axios from 'axios';
import { mapGetters } from 'vuex';
import Storage from '@homeservices/tool-kit/storage/local-storage';
import {
  getWorkOrder,
  getWorkOrderMaterials,
} from '@homeservices/api-kit/workorder';
import {
  getAssociatedServiceAttributes,
} from '@homeservices/api-kit/servicecatalog/services';
import { getPRWorkOrderByHash } from '@homeservices/api-kit/workorder/proreferral';
import { JOB_REQUEST_LOCAL_STORAGE_NAME } from '@/store/modules/jobrequest';
import isZipcodeChosen from '@/utils/is-zipcode-chosen';
import { asDictionary } from '@/transformations/service-catalog-attributes';
import authWithToken from '@/mixins/auth-token';
import { getDeliveryDate, getUrgency, getNotes, getImages } from './utils/process-work-order';
import JobRequestView from './JobRequestView';

async function useExistingLeadData ({ store, leadHash }) {
  const { data: result } = await getPRWorkOrderByHash(leadHash);

  if (result && result.user) {
    store.dispatch('jobrequest/overview/setEmail', result.user.email || '');
    store.dispatch('jobrequest/overview/setFirstName', result.user.first_name || '');
    store.dispatch('jobrequest/overview/setLastName', result.user.last_name || '');
    store.dispatch('jobrequest/overview/setPhone', result.user.phone || '');
    store.dispatch('jobrequest/details/setNotes', result.description || '');
    store.commit('jobrequest/setSourceID', result.click_source || null);
    store.commit('jobrequest/setHDStore', result.hdstore.store_number || '');
    store.commit('jobrequest/setAssociateId', result.hdstore.associate_id || '');
  }
}

export default {
  name: 'job-request-wrapper',
  components: {
    JobRequestView,
  },
  mixins: [authWithToken],
  data () {
    return {
      loading: true,
    };
  },
  computed: {
    ...mapGetters('jobrequest', ['isServiceAvailable']),
    isZipcodeChosen () {
      return isZipcodeChosen(this.$route.params.zipcode);
    },
  },
  async mounted () {
    try {
      // TODO(adam): this whole thing needs to be refactored, but it needs tests first
      this.loading = true;
      const serviceId = String(this.$route.params.serviceId);
      const zipcode = String(this.$route.params.zipcode);
      const storedData = Storage.get(JOB_REQUEST_LOCAL_STORAGE_NAME, {});
      const seed = storedData.seed || Math.random().toString(36).substring(7);
      const isChanged = storedData.serviceId !== serviceId
                        || storedData.zipcode !== zipcode;

      if (isChanged) {
        // Clears the store
        this.$store.dispatch('jobrequest/clear');
        // Clears and resets the storage
        Storage.set(JOB_REQUEST_LOCAL_STORAGE_NAME, { serviceId, zipcode, seed });
      } else {
        this.$store.commit('jobrequest/load');
      }

      let { data: { results: attributes } } = await getAssociatedServiceAttributes(
        serviceId,
        { lifecycle_status: '1' }
      );

      attributes = asDictionary(attributes, 'service_attribute_name', 'service_attribute_value');
      if (attributes.redirect_hd_url === '1') {
        this.$router.replace({
          name: 'redirect',
          query: {
            redirect_url: attributes.public_url,
          },
        });

        return; // just stop - no need to continue
      }

      const { city, state, sourceID, lead: leadHash } = this.$route.query;

      if (leadHash) {
        this.$store.dispatch('jobrequest/clear');
        await useExistingLeadData({ store: this.$store, leadHash });
        this.$store.commit('jobrequest/setWorkOrderId', leadHash);
      }

      this.$store.commit(
        'jobrequest/setSourceID',
        sourceID || 'browse'
      );
      this.$store.commit('jobrequest/setLocation', { city, state });
      const { token } = this.$route.query;
      if (token) {
        await this.authenticateWithToken(token);
      }
      await this.$store.dispatch('jobrequest/fetchService', serviceId);
      await this.$store.dispatch('jobrequest/fetchAvailability', { zipcode });

      if (this.isServiceAvailable) {
        await this.$store.dispatch('jobrequest/matches/fetchPublicProfiles', { serviceId, zipcode, seed });
      }

      // TODO: Refactor after this point into its own function
      const orderId = this.$route.query.ref_work_order || this.$route.query.work_order;

      if (orderId) {
        if (!this.isServiceAvailable) {
          try {
            await axios.put('/api/updateWorkOrderAsRejected', {
              orderId,
              notes: `Rejected workorder ${orderId} on ${new Date()}`,
              reason: 'LOCATION_OR_OUTSIDE_SERVICE_AREA',
            });
          } catch (err) {
            // don't do anything - basically this api fails hard if the work order has already
            // been updated. we don't want to block a request flow if the workorder fails completely
          }
        } else {
          if (this.$route.query.ref_work_order) {
            this.$store.commit('jobrequest/setRefWorkOrderId', this.$route.query.ref_work_order);
          }

          if (this.$route.query.work_order) {
            this.$store.commit('jobrequest/setWorkOrderId', this.$route.query.work_order);
          }

          const { data: workOrder } = await getWorkOrder(orderId);

          this.$store.commit('jobrequest/setLocation', {
            city: workOrder.service_address.city,
            state: workOrder.service_address.state,
          });

          const {
            first_name = '',
            last_name = '',
            email = '',
            phones: { primary: { phone_number = '' } },
          } = workOrder.service_address;

          this.$store.dispatch('jobrequest/overview/setFirstName', first_name);
          this.$store.dispatch('jobrequest/overview/setLastName', last_name);
          this.$store.dispatch('jobrequest/overview/setEmail', email);
          this.$store.dispatch('jobrequest/overview/setPhone', phone_number);

          if (this.$route.query.work_order) {
            const { data: orders } = await getWorkOrderMaterials(this.$route.query.work_order);

            const deliveryDate = getDeliveryDate(orders);

            this.$store.dispatch('jobrequest/details/setSelectedUrgencyId', getUrgency(deliveryDate, new Date()));
            this.$store.dispatch('jobrequest/details/setNotes', getNotes(orders, deliveryDate));
            this.$store.dispatch('jobrequest/details/setUploadedImages', await getImages(orders));
          }
        }
      }

      if (token) {
        this.$store.dispatch('user/logOutWithoutClearingSession');
      }

      this.loading = false;
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  },
};
</script>
